/**
 * Breadcrumb derivado da rota atual, com fallback de override via prop.
 *
 * Estrategia:
 *   1. Override explicito: pagina passa `crumbs={[{label, to}, ...]}`.
 *   2. Auto-derivacao: percorre MODULES/PAGINAS_GLOBAIS para casar a rota e
 *      monta `Modulo > Grupo > Item` ou `Pagina`. Se nao casar, usa o ultimo
 *      segmento da URL como fallback simples.
 *
 * Visualmente: chevrons separando segmentos, ultimo segmento em destaque
 * (texto primario, nao linkavel), demais em cinza linkaveis quando a rota
 * existir.
 */
import React, { useMemo } from "react";
import { FiChevronRight, FiHome } from "react-icons/fi";
import { useLocation } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { rotaDeInicio } from "../../utils/rotas";

import { MODULES, PAGINAS_GLOBAIS } from "../../utils/modules";

import { Crumb, CrumbLink, CrumbText, Separator, Trail } from "./styles";

function isPathMatch(itemTo, pathname) {
  return pathname === itemTo || pathname.startsWith(`${itemTo}/`);
}

function derivarBreadcrumbDaRota(pathname) {
  // Tenta achar primeiro nas paginas globais (Inicio, Clientes).
  for (const pg of PAGINAS_GLOBAIS) {
    if (isPathMatch(pg.to, pathname)) {
      return [{ label: pg.label, to: pg.to }];
    }
  }

  // Tenta achar nos modulos -> grupos -> (subgrupos ->) items.
  for (const modulo of MODULES) {
    for (const grupo of modulo.grupos) {
      for (const item of grupo.itens || []) {
        if (isPathMatch(item.to, pathname)) {
          const trail = [{ label: modulo.label }];
          if (grupo.label) trail.push({ label: grupo.label });
          trail.push({ label: item.label, to: item.to });
          return trail;
        }
      }
      // Nivel de subgrupo (2026-08-04): "Legado > Projetos Executivos >
      // Análises > Visão geral" — o subgrupo entra na trilha.
      for (const sub of grupo.subgrupos || []) {
        for (const item of sub.itens) {
          if (isPathMatch(item.to, pathname)) {
            const trail = [{ label: modulo.label }];
            if (grupo.label) trail.push({ label: grupo.label });
            trail.push({ label: sub.label });
            trail.push({ label: item.label, to: item.to });
            return trail;
          }
        }
      }
    }
  }

  // Fallback: nao casou em nada conhecido. Mostra so o ultimo segmento.
  const segmentos = pathname.split("/").filter(Boolean);
  if (segmentos.length === 0) return [];
  const ultimo = segmentos[segmentos.length - 1];
  return [{ label: ultimo.replace(/-/g, " ") }];
}

export default function Breadcrumb({ crumbs, hideOnHome = true }) {
  const location = useLocation();
  const { temModulo } = useAuth() || {};

  const trail = useMemo(() => {
    if (Array.isArray(crumbs) && crumbs.length > 0) return crumbs;
    return derivarBreadcrumbDaRota(location.pathname);
  }, [crumbs, location.pathname]);

  // Esconde em /home ou se trail vazio.
  if (trail.length === 0) return null;
  if (hideOnHome && location.pathname === "/home" && trail.length === 1) return null;

  return (
    <Trail aria-label="Breadcrumb">
      <Crumb>
        <CrumbLink to={rotaDeInicio(temModulo)} title="Início">
          <FiHome size={12} aria-hidden="true" />
        </CrumbLink>
      </Crumb>
      {trail.map((crumb, idx) => {
        const ultimo = idx === trail.length - 1;
        return (
          <React.Fragment key={`${crumb.label}-${idx}`}>
            <Separator aria-hidden="true">
              <FiChevronRight size={11} />
            </Separator>
            <Crumb>
              {ultimo ? (
                <CrumbText aria-current="page">{crumb.label}</CrumbText>
              ) : crumb.to ? (
                <CrumbLink to={crumb.to}>{crumb.label}</CrumbLink>
              ) : (
                <CrumbText $muted>{crumb.label}</CrumbText>
              )}
            </Crumb>
          </React.Fragment>
        );
      })}
    </Trail>
  );
}
